import produce from 'immer';

export type Sequences = {
  id: string;
  templateType: 'sequences';
  title: {
    update: (html: string) => void;
    text: string;
  };
  order: (newOrder: string[]) => void;
  add: () => void;
  steps: Array<{
    id: string;
    update: (html: string) => void;
    text: string;
    image: {
      url: string;
      delete: () => void;
      add: (url: string, imageId: string) => void;
      altTag: string;
    };
    sound: {
      url: string;
      delete: () => void;
      add: (url: string, soundId: string) => void;
    };
    position: number;
    delete: (facetId: string) => void;
  }>;
};

export default function sequences({ item, facets, setId, setItem, itemsService }): Sequences {
  const updateTitle = (html) => {
    itemsService.debouncedUpdate({
      pathParams: { id: item.id },
      data: { data: { attributes: { title: String(html) } } },
      debounceKey: 'sequence_title',
    });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.title.text = String(html);
      })
    );
  };

  const updateText = (facetId, html) => {
    facets.debouncedUpdate({
      pathParams: { id: facetId },
      data: { data: { relationships: { association: { data: { attributes: { 'text-html': String(html) } } } } } },
      debounceKey: `sequence_text_${facetId}`,
    });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const step = draft.steps.find((a) => a.id === facetId);
        if (step) {
          step.text = html;
        }
      })
    );
  };

  const order = (newOrder) => {
    facets.order({
      pathParams: { id: item.id },
      data: {
        ordering: newOrder.map(Number),
        set_id: Number(setId),
      },
    });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.steps = newOrder.map((id) => draft.steps.find((f) => f.id === id));
      })
    );
  };

  const newStep = (id) => {
    const position = item.facets.length + 1;
    return {
      id,
      text: '',
      image: {
        url: '',
      },
      sound: {
        url: '',
      },
      position,
    };
  };

  const add = async () => {
    const tempId = `placeholder-id-${Date.now()}`;
    const step = newStep(tempId);
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.steps = [...draft.steps, step];
      })
    );
    const newItem = await facets.create({ pathParams: { id: item.id }, urlParams: { include: undefined } });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const step = draft.steps.find((a) => a.id === tempId);
        if (step) {
          step.id = newItem.id;
          step.delete = () => deleteStep(newItem.id);
          step.update = (html) => updateText(newItem.id, html);
          step.image.add = (url, imageId) => addImage(newItem.id, url, imageId);
          step.image.delete = () => deleteImage(newItem.id);
          step.sound.add = (url, soundId) => addSound(newItem.id, url, soundId);
          step.sound.delete = () => deleteSound(newItem.id);
        }
      })
    );
  };

  const deleteStep = (id) => {
    facets.delete({ pathParams: { id } });
    setItem((prev) =>
      produce(prev, (draft) => {
        draft.steps = draft.steps.filter((a) => a.id !== id);
      })
    );
  };

  const addImage = async (facetId, url, imageId) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        const stepIndex = draft.steps.findIndex((a) => a.id === facetId);
        if (stepIndex > -1) {
          draft.steps[stepIndex].image.url = url;
        }
      })
    );
    await facets.update({
      pathParams: { id: facetId },
      data: {
        data: { relationships: { association: { data: { relationships: { image: { data: { id: imageId } } } } } } },
      },
    });
  };

  const deleteImage = (facetId) => {
    facets.update({
      pathParams: { id: facetId },
      data: {
        data: { relationships: { association: { data: { relationships: { image: { data: { id: null } } } } } } },
      },
    });
    setItem((prev) =>
      produce(prev, (draft) => {
        const stepsIndex = draft.steps.findIndex((a) => a.id === facetId);
        if (stepsIndex > -1) {
          draft.steps[stepsIndex].image.url = undefined;
        }
      })
    );
  };
  
  const addSound = async (facetId, url, soundId) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        const stepIndex = draft.steps.findIndex((a) => a.id === facetId);
        if (stepIndex > -1) {
          draft.steps[stepIndex].sound.url = url;
        }
      })
    );
    await facets.update({
      pathParams: { id: facetId },
      data: {
        data: { relationships: { association: { data: { relationships: { sound: { data: { id: soundId } } } } } } },
      },
    });
  };

  const deleteSound = (facetId) => {
    facets.update({
      pathParams: { id: facetId },
      data: {
        data: { relationships: { association: { data: { relationships: { sound: { data: { id: null } } } } } } },
      },
    });
    setItem((prev) =>
      produce(prev, (draft) => {
        const stepsIndex = draft.steps.findIndex((a) => a.id === facetId);
        if (stepsIndex > -1) {
          draft.steps[stepsIndex].sound.url = undefined;
        }
      })
    );
  };

  const initialSort = (a, b) => a.anchor.position - b.anchor.position;

  return {
    id: item.id,
    templateType: item.templateType,
    title: {
      update: updateTitle,
      text: item.title,
    },
    order,
    add,
    steps: item.facets.sort(initialSort).map((facet) => ({
      id: facet.id,
      delete: () => deleteStep(facet.id),
      update: (html) => updateText(facet.id, html),
      text: facet.association.textHtml,
      image: {
        url: facet.association.image?.smallUrl || facet.association.image?.url,
        delete: () => deleteImage(facet.id),
        add: (url, imageId) => addImage(facet.id, url, imageId),
      },
      sound: {
        url: facet.association.sound?.url,
        delete: () => deleteSound(facet.id),
        add: (url, soundId) => addSound(facet.id, url, soundId),
      },
      position: facet.anchor.position,
    })),
  };
}
